import React, { useState, useEffect } from 'react';
import { Form, FormElement } from '@progress/kendo-react-form';
import { Button } from '@progress/kendo-react-buttons';
import { Field } from '@progress/kendo-react-form';
import {
  FormInput,
  MessageAlert,
  userNameValidator,
  passwordValidator,
  Loader,
} from 'smart-react';
import { useAuth } from '../../Core/Context/AuthContext';
import { EXCEPTION_STATUS_CODES } from '../../../constants/applicationConstants';
import { callApi } from '../../../Utils/Api/Api';
import {
  ACCESS_TOKEN,
  REFRESH_TOKEN,
  TOKEN_EXP,
  USER_DATA,
} from '../../../constants/applicationConstants';
import './Splash.scss';
import { useNavigate } from 'react-router-dom';
import AzureLoginButton from './../Components/LoginWithAzure';
import GoogleLoginButton from '../Components/LoginWithGoogle';
import OktaLoginButton from '../Components/LoginWithOkta';
import OpenIdLoginButton from '../Components/LoginWithOpenId';
import { Eye, EyeOff } from 'lucide-react';
const dashboardPath = '/';

const LoginForm = ({ setIsLoader }) => {
  const navigate = useNavigate();
  const [formState, setFormState] = useState({});
  const { updateUser, checkTokens } = useAuth();
  const [isAuthProviderLoading, setisAuthProviderLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [authProviders, setAuthProviders] = useState({
    AUTH_AZURE: '0',
    AUTH_GOOGLE: '0',
    AUTH_OKTA: '0',
    AUTH_OPENID: '0',
  });
  const [alertMessage, setAlertMessage] = useState({
    alert: false,
    message: null,
  });

  useEffect(() => {
    if (checkTokens()) {
      navigate(dashboardPath);
    }
  }, [checkTokens, navigate]);

  const handleSubmit = async (dataItem) => {
    setIsLoader(true);
    dataItem = {
      ...dataItem,
      // client_id: process.env.CLIENT_ID,
      // client_secret: process.env.CLIENT_SECRET,
      device_id: navigator.userAgent,
      browser: navigator.userAgent,
      os: navigator.platform,
    };
    try {
      let response = await callApi('/login', 'POST', dataItem);
      if (response?.isSuccess) {
        const data = response.payload;
        sessionStorage.setItem(ACCESS_TOKEN, data.token.access_token);
        sessionStorage.setItem(REFRESH_TOKEN, data.token.refresh_token);
        sessionStorage.setItem(TOKEN_EXP, data.token.expires_in);
        sessionStorage.setItem(USER_DATA, JSON.stringify(data));
        updateUser(data);
        window.location.replace(dashboardPath);
      } else {
        if (
          response?.statusCode?.code &&
          !EXCEPTION_STATUS_CODES.includes(response?.statusCode?.code)
        ) {
          setAlertMessage({ alert: true, message: response?.message });
        }
      }
    } catch (e) {
      console.error('Login error:', e);
    } finally {
      setIsLoader(false);
    }
  };
  useEffect(() => {
    const fetchAuthProviders = async () => {
      try {
        const response = await callApi('/auth/providers', 'GET');

        setAuthProviders(response);
      } catch (error) {
        console.error('Error fetching auth providers:', error);
      } finally {
        setisAuthProviderLoading(false);
      }
    };

    fetchAuthProviders();
  }, []);
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  return (
    <>
      {alertMessage?.alert && (
        <MessageAlert
          type={'danger'}
          message={alertMessage?.message}
          isIcon={true}
          iconName={'error'}
        />
      )}

      <Form
        initialValues={formState}
        onSubmit={handleSubmit}
        render={(formRenderProps) => (
          <FormElement>
            <div>
              <Field
                key={'username'}
                id={'username'}
                name={'username'}
                label={'Username'}
                labelClass={'k-font-bold'}
                inputClass={'k-login-input rollout-input'}
                type={'text'}
                validator={userNameValidator}
                component={FormInput}
                optional={false}
              />
              <div style={{ position: 'relative' }}>
                <Field
                  key={'password'}
                  id={'password'}
                  name={'password'}
                  label={'Password'}
                  labelClass={'k-font-bold'}
                  inputClass={'k-login-input'}
                  type={showPassword ? "text" : "password"}
                  component={FormInput}
                  validator={passwordValidator}
                  optional={false}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#666',
                    zIndex: 1,
                  }}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div
              style={{
                justifyContent: 'space-between',
                alignContent: 'center',
              }}
              className={
                'k-form-buttons k-button k-button-md k-rounded-md k-button-solid k-button-solid-bases-end'
              }
            >
              <Button
                className="k-w-100"
                themeColor={'dark'}
                type={'submit'}
                rounded={'medium'}
                size={'medium'}
              >
                Login
              </Button>
            </div>
          </FormElement>
        )}
      />
      <div className="k-form k-form-md">
        {!isAuthProviderLoading &&
          (authProviders.AUTH_AZURE === '1' ||
            authProviders.AUTH_GOOGLE === '1' ||
            authProviders.AUTH_OKTA === '1' ||
            authProviders.AUTH_OPENID === '1') && <hr />}
        {isAuthProviderLoading && <Loader />}
        {!isAuthProviderLoading && authProviders.AUTH_AZURE === '1' && (
          <AzureLoginButton
            setIsLoader={setIsLoader}
            setAlertMessage={setAlertMessage}
          />
        )}

        {!isAuthProviderLoading && authProviders.AUTH_GOOGLE === '1' && (
          <GoogleLoginButton
            setIsLoader={setIsLoader}
            setAlertMessage={setAlertMessage}
          />
        )}
        {!isAuthProviderLoading && authProviders.AUTH_OKTA === '1' && (
          <OktaLoginButton
            setIsLoader={setIsLoader}
            setAlertMessage={setAlertMessage}
          />
        )}
        {!isAuthProviderLoading && authProviders.AUTH_OPENID === '1' && (
          <OpenIdLoginButton
            setIsLoader={setIsLoader}
            setAlertMessage={setAlertMessage}
          />
        )}
      </div>
    </>
  );
};

export default LoginForm;
